import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import QrReader from "react-qr-reader";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQrcode } from "@fortawesome/free-solid-svg-icons";

import pako from "pako";

import { Button } from "@wfp/ui";

import { getCurrentSchoolProfileWfpCommodities } from "data-handler/ducks/schools";
import { TOAST_ERROR, popToast } from "data-handler/ducks/toast";
import { MeasureUnit } from "data-handler/types/stores";
import { deliveryCategory } from "SCConstants";

interface CommodityOption {
  name: string;
  id: number;
  is_wfp: boolean;
  measure_unit: MeasureUnit;
  commodity_gateway: {
    code: string;
  };
}

interface ScannedCommodity {
  batch_no: string;
  commodity: string;
  netUnitWeight: string;
}

interface QRCodeReaderProps {
  getValues: () => Record<string, unknown>;
  reset: (values: Record<string, unknown>) => void;
  qrCodeParam: boolean;
}

const QrCodeReader: React.FC<QRCodeReaderProps> = ({
  getValues,
  reset,
  qrCodeParam,
}: QRCodeReaderProps) => {
  const dispatch = useDispatch();
  const [useQrCode, setQrCode] = useState(qrCodeParam);
  const commodityListWfp: CommodityOption[] = useSelector(
    getCurrentSchoolProfileWfpCommodities
  );

  const activateQrCode = () => {
    setQrCode(true);
  };

  const handleError = (e: Error) => {
    dispatch(popToast(TOAST_ERROR, e, {}, {}));
    setQrCode(false);
  };

  const handleScan = (e: string | null) => {
    try {
      // When no Code is present, e is null
      if (e !== null) {
        // Decode base64 (convert ascii to binary)
        let strData = atob(e);
        // Convert binary string to character-number array
        let charData = strData.split("").map(function (x) {
          return x.charCodeAt(0);
        });
        // Turn number array into byte-array
        let binData = new Uint8Array(charData);
        // Pako magic
        let data = pako.inflate(binData);
        // Convert gunzipped byteArray back to ascii string:
        strData = String.fromCharCode(...Array.from(new Uint16Array(data)));

        const parsedStrData = strData.split("#");
        const header = parsedStrData.shift() || "";
        const waybillNo = header.split(";")[0];
        const qrStockEvents: Array<{
          category: string;
          issues: never[];
          batch_no: { label: string; value: string };
          quantity: string;
          commodity: number;
          measure_unit: MeasureUnit;
        }> = [];
        const errorCommodities: string[] = [];

        parsedStrData.forEach((item, index) => {
          const qrData = item.split(";");

          const { batch_no, commodity, netUnitWeight }: ScannedCommodity = {
            batch_no: qrData[1],
            commodity: qrData[3],
            netUnitWeight: (Number(qrData[4]) * Number(qrData[6])).toFixed(3), // Number of units * Net Unit Weight
          };

          const matchingCommmodity = commodityListWfp.find(
            (e) => e.commodity_gateway?.code === commodity
          );

          if (!batch_no) {
            errorCommodities.push(
              `Index ${index} - cannot read batch number from QR code`
            );
          } else if (batch_no && !matchingCommmodity) {
            if (commodity) {
              errorCommodities.push(
                `Index ${index} - Batch number ${batch_no} - no matching commodity in the school profile for code ${commodity}`
              );
            } else {
              errorCommodities.push(
                `Index ${index} - Batch number ${batch_no} - cannot read commodity from QR code`
              );
            }
          } else if (batch_no && matchingCommmodity && netUnitWeight) {
            qrStockEvents.push({
              category: deliveryCategory,
              issues: [],
              batch_no: { label: batch_no, value: batch_no },
              quantity: netUnitWeight,
              commodity: matchingCommmodity.id,
              measure_unit: matchingCommmodity.measure_unit,
            });
          } else {
            errorCommodities.push(
              `Index ${index} - Cannot import batch number ${batch_no}`
            );
          }
        });

        // Need to reset the default values of the Form in order to have the fields load the QR Code data.
        const resetValues: Record<string, unknown> = {
          ...getValues(),
          waybill_no: waybillNo,
        };
        if (qrStockEvents.length > 0) {
          resetValues.stock_events = qrStockEvents;
        }
        reset(resetValues);

        // Display error if any of the entries cannot be loaded
        if (errorCommodities.length > 0) {
          let errorString = "";
          errorCommodities.forEach((e) => {
            if (errorString === "") {
              errorString = e;
            } else {
              errorString = e + ", " + errorString;
            }
          });
          const errorMessage = () => (
            <FormattedMessage
              id="qrCode.errorEntry"
              defaultMessage="Unable to import delivery: {error}. Please enter manually"
              values={{ error: errorString }}
            />
          );
          dispatch(popToast(TOAST_ERROR, errorMessage, {}, {}));
        }

        setQrCode(false);
      }
    } catch (e) {
      dispatch(popToast(TOAST_ERROR, e, {}, {}));
      setQrCode(false);
    }
  };

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faQrcode} />}
        kind="secondary"
        onClick={activateQrCode}
      >
        <FormattedMessage
          id="QrCodeReader.scanQrCode"
          defaultMessage="Scan QR code"
        />
      </Button>
      {useQrCode ? (
        <div className="qr-code">
          <QrReader
            delay={300}
            onError={handleError}
            onScan={handleScan}
            style={{ width: "100%" }}
          />
          <Button kind="inverse" small onClick={() => setQrCode(false)}>
            <FormattedMessage id="QrCodeReader.close" defaultMessage="Close" />
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default QrCodeReader;
